import React, { useState, useEffect,useRef } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Calendar, Building2, Users, AlertCircle, Package, Shield, CreditCard, FileText, RefreshCw, Download, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import "../../Styling/CFODashboard.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";


const CFODashboard = () => {
  const [filters, setFilters] = useState({
    orgId: 'ORG-001',
    companyCode: 'COMP-2024',
    fiscalYear: '2024-2025',
    postingDate: '2024-10-18'
  });

  const [dashboardData, setDashboardData] = useState({
    bankBalances: [],
    assetsLiabilities: {},
    paymentsDue: [],
    revenueExpected: [],
    financialTrends: [],
    insurancePolicies: [],
    inventoryAssets: [],
    revenuePerPerson: [],
    fixedExpenses: [],
    gstDues: []
  });
const dashboardRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const mockData = await fetchMockData();
      setDashboardData(mockData);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({
        bankBalances: [],
        assetsLiabilities: {},
        paymentsDue: [],
        revenueExpected: [],
        financialTrends: [],
        insurancePolicies: [],
        inventoryAssets: [],
        revenuePerPerson: [],
        fixedExpenses: [],
        gstDues: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Mock data fetcher
  const fetchMockData = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      bankBalances: [
        { bank: 'HDFC Bank', accountNo: '****1234', balance: 2450000, type: 'Current', trend: 'up' },
        { bank: 'ICICI Bank', accountNo: '****5678', balance: 1850000, type: 'Savings', trend: 'up' },
        { bank: 'SBI Bank', accountNo: '****9012', balance: 980000, type: 'Current', trend: 'down' },
        { bank: 'Axis Bank', accountNo: '****3456', balance: 650000, type: 'Savings', trend: 'up' }
      ],
      assetsLiabilities: {
        totalAssets: 12500000,
        currentAssets: 5800000,
        fixedAssets: 6700000,
        totalLiabilities: 4200000,
        currentLiabilities: 2100000,
        longTermLiabilities: 2100000
      },
      paymentsDue: [
        { category: 'Vendor Payments', amount: 450000, dueDate: '2024-10-25', priority: 'high' },
        { category: 'Salary & Wages', amount: 920000, dueDate: '2024-10-28', priority: 'critical' },
        { category: 'Rent & Utilities', amount: 185000, dueDate: '2024-10-30', priority: 'medium' },
        { category: 'Loan EMI', amount: 275000, dueDate: '2024-10-31', priority: 'high' },
        { category: 'Tax Payments', amount: 340000, dueDate: '2024-11-05', priority: 'medium' }
      ],
      revenueExpected: [
        { client: 'Acme Corp', amount: 680000, expectedDate: '2024-10-20', confidence: 'high' },
        { client: 'TechStart Inc', amount: 520000, expectedDate: '2024-10-25', confidence: 'medium' },
        { client: 'Global Industries', amount: 890000, expectedDate: '2024-11-01', confidence: 'high' },
        { client: 'RetailMax Ltd', amount: 450000, expectedDate: '2024-11-08', confidence: 'low' }
      ],
      financialTrends: [
        { month: 'Apr', openAR: 2100000, badDebts: 85000, accountsPayable: 1450000, bankBalance: 4200000 },
        { month: 'May', openAR: 2350000, badDebts: 92000, accountsPayable: 1580000, bankBalance: 4580000 },
        { month: 'Jun', openAR: 2180000, badDebts: 78000, accountsPayable: 1420000, bankBalance: 4920000 },
        { month: 'Jul', openAR: 2520000, badDebts: 105000, accountsPayable: 1680000, bankBalance: 5150000 },
        { month: 'Aug', openAR: 2380000, badDebts: 88000, accountsPayable: 1590000, bankBalance: 5480000 },
        { month: 'Sep', openAR: 2650000, badDebts: 98000, accountsPayable: 1720000, bankBalance: 5680000 },
        { month: 'Oct', openAR: 2540000, badDebts: 82000, accountsPayable: 1650000, bankBalance: 5930000 }
      ],
      insurancePolicies: [
        { policyName: 'General Liability Insurance', provider: 'HDFC ERGO', expiryDate: '2024-11-15', premium: 285000, status: 'Expiring Soon' },
        { policyName: 'Property Insurance', provider: 'ICICI Lombard', expiryDate: '2024-12-20', premium: 420000, status: 'Active' },
        { policyName: 'Directors & Officers', provider: 'Bajaj Allianz', expiryDate: '2024-10-30', premium: 650000, status: 'Critical' },
        { policyName: 'Cyber Insurance', provider: 'Tata AIG', expiryDate: '2025-01-10', premium: 380000, status: 'Active' }
      ],
      inventoryAssets: [
        { category: 'Raw Materials', value: 1850000, trend: 'up' },
        { category: 'Finished Goods', value: 2450000, trend: 'down' },
        { category: 'IT Equipment', value: 1200000, trend: 'stable' },
        { category: 'Office Furniture', value: 680000, trend: 'stable' },
        { category: 'Vehicles', value: 1520000, trend: 'up' }
      ],
      revenuePerPerson: [
        { department: 'Sales', employees: 15, revenue: 4200000, perPerson: 280000, trend: 'up' },
        { department: 'Consulting', employees: 8, revenue: 3200000, perPerson: 400000, trend: 'up' },
        { department: 'Support', employees: 12, revenue: 1800000, perPerson: 150000, trend: 'stable' },
        { department: 'Marketing', employees: 6, revenue: 900000, perPerson: 150000, trend: 'down' }
      ],
      fixedExpenses: [
        { category: 'Salaries', amount: 920000, color: '#3b82f6' },
        { category: 'Rent', amount: 150000, color: '#8b5cf6' },
        { category: 'Utilities', amount: 35000, color: '#ec4899' },
        { category: 'Software Licenses', amount: 85000, color: '#10b981' },
        { category: 'Insurance', amount: 65000, color: '#f59e0b' },
        { category: 'Maintenance', amount: 45000, color: '#ef4444' }
      ],
      gstDues: [
        { type: 'GST - CGST', amount: 125000, dueDate: '2024-10-20', status: 'Pending' },
        { type: 'GST - SGST', amount: 125000, dueDate: '2024-10-20', status: 'Pending' },
        { type: 'GST - IGST', amount: 90000, dueDate: '2024-10-20', status: 'Pending' },
        { type: 'TDS Payable', amount: 180000, dueDate: '2024-11-07', status: 'Upcoming' },
        { type: 'Professional Tax', amount: 15000, dueDate: '2024-10-31', status: 'Pending' }
      ]
    };
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleInputChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    fetchDashboardData();
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Calculate totals
  const totalBankBalance = dashboardData.bankBalances.reduce((sum, item) => sum + (item.balance || 0), 0);
  const totalPaymentsDue = dashboardData.paymentsDue.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalRevenueExpected = dashboardData.revenueExpected.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netWorth = (dashboardData.assetsLiabilities.totalAssets || 0) - (dashboardData.assetsLiabilities.totalLiabilities || 0);
  const totalInventoryValue = dashboardData.inventoryAssets.reduce((sum, item) => sum + (item.value || 0), 0);

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Critical': return 'status-critical';
      case 'Expiring Soon': return 'status-warning';
      case 'Pending': return 'status-pending';
      case 'Upcoming': return 'status-info';
      case 'Active': return 'status-success';
      default: return 'status-default';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUp className="w-3 h-3 text-green-500" />;
      case 'down': return <ArrowDown className="w-3 h-3 text-red-500" />;
      default: return <span className="w-3 h-3 text-gray-400">•</span>;
    }
  };

  // KPI Cards Data
  const kpiCards = [
    {
      id: 'bank-balance',
      title: 'Total Bank Balance',
      value: totalBankBalance,
      icon: DollarSign,
      color: 'blue',
      trend: 'up',
      change: '+12.5%'
    },
    {
      id: 'net-worth',
      title: 'Net Worth',
      value: netWorth,
      icon: TrendingUp,
      color: 'green',
      trend: 'up',
      change: '+8.2%'
    },
    {
      id: 'payments-due',
      title: 'Payments Due',
      value: totalPaymentsDue,
      icon: AlertCircle,
      color: 'orange',
      trend: 'down',
      change: '-3.1%'
    },
    {
      id: 'revenue-expected',
      title: 'Revenue Expected',
      value: totalRevenueExpected,
      icon: CreditCard,
      color: 'purple',
      trend: 'up',
      change: '+15.7%'
    },
    {
      id: 'inventory-value',
      title: 'Inventory Value',
      value: totalInventoryValue,
      icon: Package,
      color: 'pink',
      trend: 'stable',
      change: '+2.4%'
    }
  ];
const handleDownloadPDF = async () => {
  if (!dashboardRef.current) return;

  const canvas = await html2canvas(dashboardRef.current, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff"
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

  let heightLeft = pdfHeight;
  let position = 0;

  pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
  heightLeft -= pdf.internal.pageSize.getHeight();

  while (heightLeft > 0) {
    position = heightLeft - pdfHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();
  }

  pdf.save(`CFO_Dashboard_${filters.fiscalYear}.pdf`);
};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1920px] mx-auto" ref={dashboardRef}>

        {/* Header */}
        <div className="mb-6 bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover-card">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">CFO Financial Dashboard</h1>
              <p className="text-gray-600">Executive Financial Overview & Key Metrics - FY {filters.fiscalYear}</p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <span className="text-sm text-gray-500">Last updated: {lastUpdated}</span>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Refresh'}
              </button>
             <button
          onClick={handleDownloadPDF}
          className="btn-secondary flex items-center gap-2"
        >
        <Download className="w-4 h-4" />
        Export
      </button>

            </div>
          </div>
        </div>

        {/* Enhanced Filter Section - Single Line Layout */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-200 hover-card">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-blue-600">
              <Building2 className="w-5 h-5" />
              <span className="font-semibold text-lg">Filters</span>
            </div>
            
            <div className="flex items-center gap-4 flex-1">
              <div className="flex flex-col flex-1">
                <label className="filter-label">Organization ID</label>
                <input
                  type="text"
                  name="orgId"
                  value={filters.orgId}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter Organization ID"
                />
              </div>
              
              <div className="flex flex-col flex-1">
                <label className="filter-label">Company Code</label>
                <input
                  type="text"
                  name="companyCode"
                  value={filters.companyCode}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter Company Code"
                />
              </div>
              
              <div className="flex flex-col flex-1">
                <label className="filter-label">Fiscal Year</label>
                <input
                  type="text"
                  name="fiscalYear"
                  value={filters.fiscalYear}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter Fiscal Year"
                />
              </div>
              
              <div className="flex flex-col flex-1">
                <label className="filter-label">Posting Date</label>
                <input
                  type="date"
                  name="postingDate"
                  value={filters.postingDate}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>
            </div>
            
            <div className="flex flex-col justify-end h-full pt-6">
              <button
                onClick={handleApplyFilters}
                className="btn-primary-small"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards - Single Line */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          {kpiCards.map((kpi) => (
            <div
              key={kpi.id}
              className={`kpi-card kpi-${kpi.color} hover-lift`}
              onMouseEnter={() => setHoveredCard(kpi.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="kpi-header">
                <div className={`kpi-icon kpi-icon-${kpi.color}`}>
                  <kpi.icon className="w-5 h-5" />
                </div>
                <div className="kpi-trend">
                  {getTrendIcon(kpi.trend)}
                  <span className={`text-xs font-medium ${
                    kpi.trend === 'up' ? 'text-green-600' : 
                    kpi.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {kpi.change}
                  </span>
                </div>
              </div>
              <div className="kpi-content">
                <p className="kpi-value">{formatCurrency(kpi.value)}</p>
                <p className="kpi-label">{kpi.title}</p>
              </div>
              {hoveredCard === kpi.id && (
                <div className="kpi-hover-effect"></div>
              )}
            </div>
          ))}
        </div>

        {/* Rest of the dashboard components remain the same */}
        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Bank Balances */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Bank Balances
            </h3>
            <div className="space-y-3">
              {dashboardData.bankBalances.map((bank, idx) => (
                <div key={idx} className="list-item hover:border-blue-300">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="item-title">{bank.bank}</p>
                      <p className="item-subtitle">{bank.accountNo} • {bank.type}</p>
                    </div>
                    <div className="text-right">
                      <p className="item-value text-green-600">{formatCurrency(bank.balance)}</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        {getTrendIcon(bank.trend)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assets & Liabilities */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <FileText className="w-5 h-5 text-purple-600" />
              Assets & Liabilities
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[
                { name: 'Assets', Current: dashboardData.assetsLiabilities.currentAssets / 100000, Fixed: dashboardData.assetsLiabilities.fixedAssets / 100000 },
                { name: 'Liabilities', Current: dashboardData.assetsLiabilities.currentLiabilities / 100000, 'Long Term': dashboardData.assetsLiabilities.longTermLiabilities / 100000 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `₹${value}L`}
                />
                <Legend />
                <Bar dataKey="Current" fill="#3b82f6" />
                <Bar dataKey="Fixed" fill="#8b5cf6" />
                <Bar dataKey="Long Term" fill="#ec4899" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Payments Due Monthly */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <Calendar className="w-5 h-5 text-orange-600" />
              Payments Due This Month
            </h3>
            <div className="space-y-2">
              {dashboardData.paymentsDue.map((payment, idx) => (
                <div key={idx} className="list-item hover:border-orange-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="item-title">{payment.category}</p>
                      <p className="item-subtitle">Due: {payment.dueDate}</p>
                    </div>
                    <p className="item-value text-orange-600">{formatCurrency(payment.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Additional sections continue... */}
        {/* Revenue Expected */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Revenue Expected
            </h3>
            <div className="space-y-2">
              {dashboardData.revenueExpected.map((rev, idx) => (
                <div key={idx} className="list-item hover:border-green-300">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="item-title">{rev.client}</p>
                      <p className="item-subtitle">Expected: {rev.expectedDate}</p>
                    </div>
                    <p className="item-value text-green-600">{formatCurrency(rev.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Financial Trends */}
          <div className="dashboard-card hover-lift col-span-2">
            <h3 className="section-title">Financial Trends</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dashboardData.financialTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `₹${(value / 100000).toFixed(2)}L`}
                />
                <Legend />
                <Line type="monotone" dataKey="openAR" stroke="#3b82f6" strokeWidth={2} name="Open AR" />
                <Line type="monotone" dataKey="badDebts" stroke="#ef4444" strokeWidth={2} name="Bad Debts" />
                <Line type="monotone" dataKey="accountsPayable" stroke="#f59e0b" strokeWidth={2} name="AP" />
                <Line type="monotone" dataKey="bankBalance" stroke="#10b981" strokeWidth={2} name="Bank Balance" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Insurance Policies */}
        <div className="dashboard-card hover-lift mb-6">
          <h3 className="section-title">
            <Shield className="w-5 h-5 text-red-600" />
            Insurance & Policies Near Expiry
          </h3>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Policy Name</th>
                  <th>Provider</th>
                  <th>Expiry Date</th>
                  <th className="text-right">Premium</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.insurancePolicies.map((policy, idx) => (
                  <tr key={idx} className="hover-row">
                    <td className="item-title">{policy.policyName}</td>
                    <td className="item-subtitle">{policy.provider}</td>
                    <td className="item-subtitle">{policy.expiryDate}</td>
                    <td className="text-right item-value">{formatCurrency(policy.premium)}</td>
                    <td className="text-center">
                      <span className={`status-badge ${getStatusColor(policy.status)}`}>
                        {policy.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Inventory & Asset Value */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <Package className="w-5 h-5 text-blue-600" />
              Inventory & Asset Value
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dashboardData.inventoryAssets} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" style={{ fontSize: '11px' }} />
                <YAxis dataKey="category" type="category" stroke="#6b7280" width={100} style={{ fontSize: '11px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `₹${(value / 100000).toFixed(2)}L`}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue Per Person */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <Users className="w-5 h-5 text-purple-600" />
              Revenue Per Person
            </h3>
            <div className="space-y-3">
              {dashboardData.revenuePerPerson.map((dept, idx) => (
                <div key={idx} className="list-item">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="item-title">{dept.department}</p>
                      <p className="item-subtitle">{dept.employees} employees</p>
                    </div>
                    <div className="text-right">
                      <p className="item-value text-purple-600">{formatCurrency(dept.perPerson)}</p>
                      <div className="flex items-center justify-end gap-1 mt-1">
                        {getTrendIcon(dept.trend)}
                      </div>
                    </div>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill bg-purple-500" 
                      style={{ width: `${Math.min((dept.perPerson / 400000) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed Expenses */}
          <div className="dashboard-card hover-lift">
            <h3 className="section-title">
              <CreditCard className="w-5 h-5 text-orange-600" />
              Fixed Expenses Per Month
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={dashboardData.fixedExpenses}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={85}
                  fill="#8884d8"
                  dataKey="amount"
                >
                  {dashboardData.fixedExpenses.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `₹${value.toLocaleString()}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* GST Dues */}
        <div className="dashboard-card hover-lift">
          <h3 className="section-title">
            <FileText className="w-5 h-5 text-yellow-600" />
            GST and Other Government Dues
          </h3>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th className="text-right">Amount</th>
                  <th>Due Date</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.gstDues.map((gst, idx) => (
                  <tr key={idx} className="hover-row">
                    <td className="item-title">{gst.type}</td>
                    <td className="text-right item-value text-yellow-600">{formatCurrency(gst.amount)}</td>
                    <td className="item-subtitle">{gst.dueDate}</td>
                    <td className="text-center">
                      <span className={`status-badge ${getStatusColor(gst.status)}`}>
                        {gst.status}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td className="item-title font-bold">Total Dues</td>
                  <td className="text-right item-value text-yellow-600 font-bold text-lg">
                    {formatCurrency(dashboardData.gstDues.reduce((sum, item) => sum + item.amount, 0))}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CFODashboard;